import { fetchClinicData } from '@/entities/clinic';
import {
  fetchUnreadInboxCounts,
  fetchUnreviewedReportCount,
  useInboxStore,
} from '@/entities/inbox';
import { useUserStore } from '@/entities/user';
import { useLoadingStore } from '@/shared/model';
import { EMITTER_CONSTANTS, LOADING_TYPES } from '@/shared/config';
import {
  auth,
  emitter,
  showMessageError,
  GETTEXT_ALL_FACILITIES,
  GETTEXT_ALL_PRIORITY,
  GETTEXT_ALL_STUDIES,
} from '@/shared/lib';
import {
  useMergeState,
  useUpdateDeepCompareEffect,
  useUpdateEffect,
  useElementChange,
} from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { t } from 'ttag';
import { SORT_BY_DATES } from '../config/inbox';

type InboxListRefHandle = {
  handleReloadData: () => void;
  fetchData: (...args: any[]) => void;
};

/**
 * Backs the inbox page with its active tab, per-type unread counts, list refs and the drawer and ECG viewer toggles. Seeds state from the persisted inbox store, keeps the counts current through the count queries plus emitter subscriptions for marked inboxes, inbox events and marked report statuses, and tracks window scroll and the header's login-banner class.
 *
 * @returns The inbox page state, list refs and the tab, viewer, drawer and count handlers.
 */
const useInbox = () => {
  const params = useParams();

  /**
   * Subscription to the whole inbox store, giving the inbox page its persisted tab and search goto state. Selects the entire store object, so any inbox store change re-renders the hook.
   *
   * @returns The complete inbox store state and actions.
   */
  const inboxesGotoRedux = useInboxStore((s) => s);
  /**
   * Facility currently selected by the user, read from the user store. Selects the selectedFacility field so inbox count queries can be scoped by facility.
   *
   * @returns The selected facility option held in the user store.
   */
  const selectedFacilityRedux = useUserStore((s) => s.selectedFacility);
  /**
   * Patient-scope selection, own patients versus all studies, read from the user store. Selects the selectedTypeSeePatients field, which drives the viewAll flag on inbox count queries.
   *
   * @returns The selected patient-scope option held in the user store.
   */
  const selectedTypeSeePatients = useUserStore((s) => s.selectedTypeSeePatients);

  /**
   * Loading store action that sets the global loading text. Selects setLoadingText so the inbox page can show and clear a loading overlay while fetching ECG data.
   *
   * @returns The setLoadingText action from the loading store.
   */
  const loadingPageAction = useLoadingStore((s) => s.setLoadingText);
  /**
   * Inbox store action that persists the goto state. Selects updateInboxGoto so tab changes made on the page can be written back to the store.
   *
   * @returns The updateInboxGoto action from the inbox store.
   */
  const setStateRedux = useInboxStore((s) => s.updateInboxGoto);

  const allInboxListRef = useRef<InboxListRefHandle>(null);
  const recentInboxListRef = useRef<InboxListRefHandle>(null);
  const eouInboxListRef = useRef<InboxListRefHandle>(null);
  const interimInboxListRef = useRef<InboxListRefHandle>(null);
  const eventInboxListRef = useRef<InboxListRefHandle>(null);
  const instantInboxListRef = useRef<InboxListRefHandle>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const isChangePosition = useElementChange(headerRef);

  const [state, setState] = useMergeState({
    isScroll: false,
    activeTab: inboxesGotoRedux.tab || '1',
    recentUnreadCount: 0,
    unreadEouCount: 0,
    unreadEventCount: 0,
    unreadInterimCount: 0,
    unreadAllCount: 0,
    allEventCount: 0,
    allInterimCount: 0,
    allEouCount: 0,
    allCount: 0,
    unreviewedReportCount: 0,
    inboxECGViewerData: undefined as any,
    isVisibleStudyInfoDrawer: false,
    isVisiblePatientInfoDrawer: false,
    clickedData: null as any,
    searchGoto: inboxesGotoRedux.search,
  });

  /**
   * Merges a search-goto patch into both the inbox page state and the persisted inbox store search. Skips the write when the serialized patch already equals the stored search, otherwise deep clones it, spreads it over the existing search and assigns the result back onto the store object.
   *
   * @param searchGoto - Partial search-goto values to merge into the current inbox search.
   * @returns Nothing.
   */
  const updateSearchGoto = (searchGoto: any) => {
    if (JSON.stringify(searchGoto) !== JSON.stringify(inboxesGotoRedux?.search)) {
      const clonedSearchGoto = _.cloneDeep(searchGoto);
      const updatedSearchGoto = {
        ...inboxesGotoRedux.search,
        ...clonedSearchGoto,
      };
      setState({ searchGoto: updatedSearchGoto });
      _.assign(inboxesGotoRedux, {
        search: {
          ...updatedSearchGoto,
        },
      });
    }
  };

  /**
   * Refreshes the per-type inbox counts together with the unreviewed report count. Builds a shared filter from the current search-goto values, selected facility and patient scope, runs the unread inbox count and the Generated-status report count queries in parallel, merges both into state, and surfaces a failure as an error message.
   *
   * @returns A promise resolving once both count queries have been stored.
   */
  const updateInboxCount = async () => {
    try {
      const { searchGoto } = state;
      const studyFid =
        searchGoto.searchByValue === 'studyFid'
          ? searchGoto.searchValue?.toString().trim() &&
            !_.isNaN(parseInt(searchGoto.searchValue, 10))
            ? parseInt(searchGoto.searchValue, 10)
            : undefined
          : undefined;
      const patientName =
        searchGoto.searchByValue === 'patientName' ? searchGoto.searchValue?.trim() : undefined;
      const patientId =
        searchGoto.searchByValue === 'patientId' ? searchGoto.searchValue?.trim() : undefined;
      const physicianId =
        searchGoto.searchByValue === 'physicianId'
          ? (searchGoto.physicianSearchValue as unknown as { value?: string })?.value
          : undefined;
      const priority =
        searchGoto.priority !== GETTEXT_ALL_PRIORITY ? searchGoto.priority : undefined;
      const { statusReports } = inboxesGotoRedux.search;
      const queryFilter = {
        studyFid,
        patientName,
        patientId,
        physicianId,
        priority,
        facilityId:
          selectedFacilityRedux.value !== GETTEXT_ALL_FACILITIES
            ? selectedFacilityRedux.value
            : undefined,
        viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
        statusReports,
      };
      const unreviewedQueryFilter = {
        studyFid,
        patientName,
        patientId,
        physicianId,
        facilityId:
          selectedFacilityRedux.value !== GETTEXT_ALL_FACILITIES
            ? selectedFacilityRedux.value
            : undefined,
        reportStatus: 'Generated',
        viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
      };
      const [inboxCount, unreviewedReportCount] = await Promise.all([
        fetchUnreadInboxCounts(queryFilter),
        fetchUnreviewedReportCount(unreviewedQueryFilter),
      ]);
      setState({
        unreadEouCount: inboxCount?.unreadEouCount ?? 0,
        unreadEventCount: inboxCount?.unreadEventCount ?? 0,
        unreadInterimCount: inboxCount?.unreadInterimCount ?? 0,
        allEouCount: inboxCount?.allEouCount ?? 0,
        allEventCount: inboxCount?.allEventCount ?? 0,
        allInterimCount: inboxCount?.allInterimCount ?? 0,
        unreviewedReportCount: unreviewedReportCount ?? 0,
      });
    } catch (error) {
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  /**
   * Refreshes the aggregate unread and total counts behind the inbox All tab. Builds a query filter from the current search-goto values, selected facility and patient scope, then sums the EOU, interim and event figures of the response into unreadAllCount and allCount, surfacing a failure as an error message.
   *
   * @returns A promise resolving once the counts have been fetched and stored.
   */
  const updateAllInboxCount = async () => {
    try {
      const { searchGoto } = state;
      const studyFid =
        searchGoto.searchByValue === 'studyFid'
          ? searchGoto.searchValue?.toString().trim() &&
            !_.isNaN(parseInt(searchGoto.searchValue, 10))
            ? parseInt(searchGoto.searchValue, 10)
            : undefined
          : undefined;
      const patientName =
        searchGoto.searchByValue === 'patientName' ? searchGoto.searchValue?.trim() : undefined;
      const patientId =
        searchGoto.searchByValue === 'patientId' ? searchGoto.searchValue?.trim() : undefined;
      const physicianId =
        searchGoto.searchByValue === 'physicianId'
          ? (searchGoto.physicianSearchValue as unknown as { value?: string })?.value
          : undefined;
      const priority =
        searchGoto.priority !== GETTEXT_ALL_PRIORITY ? searchGoto.priority : undefined;
      const { statusAllReports } = inboxesGotoRedux.search;
      const queryFilter = {
        studyFid,
        patientName,
        patientId,
        physicianId,
        priority,
        facilityId:
          selectedFacilityRedux.value !== GETTEXT_ALL_FACILITIES
            ? selectedFacilityRedux.value
            : undefined,
        viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
        statusReports: statusAllReports,
      };

      const inboxCount = await fetchUnreadInboxCounts(queryFilter);

      setState({
        unreadAllCount:
          (inboxCount?.unreadEouCount ?? 0) +
          (inboxCount?.unreadInterimCount ?? 0) +
          (inboxCount?.unreadEventCount ?? 0),
        allCount:
          (inboxCount?.allEouCount ?? 0) +
          (inboxCount?.allInterimCount ?? 0) +
          (inboxCount?.allEventCount ?? 0),
      });
    } catch (error) {
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  /**
   * Triggers a data reload on the inbox list belonging to the tab the user re-selected. Calls handleReloadData on the recent, all, EOU, interim, event or instant list ref for tab keys '1' through '6', and does nothing for any other key.
   *
   * @param tab - Key of the inbox tab whose list should reload.
   * @returns Nothing.
   */
  const handleReloadInboxList = (tab: string) => {
    switch (tab) {
      case '1': {
        recentInboxListRef.current?.handleReloadData();
        break;
      }
      case '2': {
        allInboxListRef.current?.handleReloadData();
        break;
      }
      case '3': {
        eouInboxListRef.current?.handleReloadData();
        break;
      }
      case '4': {
        interimInboxListRef.current?.handleReloadData();
        break;
      }
      case '5': {
        eventInboxListRef.current?.handleReloadData();
        break;
      }
      case '6': {
        instantInboxListRef.current?.handleReloadData();
        break;
      }
      default: {
        break;
      }
    }
  };

  /**
   * Switches the active inbox tab, or reloads the current list when the same tab is clicked again. Forces the sort value to the first date-based option before moving to the instant tab with a non-date sort, then either writes the new tab to state and the inbox store or reloads the matching list.
   *
   * @param tab - Key of the inbox tab to navigate to.
   * @returns Nothing.
   */
  const navigateTab = (tab: string) => {
    if (tab === '6' && !SORT_BY_DATES.includes(inboxesGotoRedux.search.sortValue)) {
      const updatedSearchGoto = {
        ...inboxesGotoRedux.search,
        sortValue: SORT_BY_DATES[0],
      };
      setState({ searchGoto: updatedSearchGoto });
      updateSearchGoto(updatedSearchGoto);
    }
    const { activeTab } = state;
    if (tab !== activeTab) {
      setState({ activeTab: tab });
      setStateRedux({ tab });
    } else {
      handleReloadInboxList(tab);
    }
  };

  /**
   * Keeps the inbox page's isScroll flag aligned with whether the window is scrolled away from the top. Writes state only on a transition, comparing window.scrollY against the flag currently held in state.
   *
   * @returns Nothing.
   */
  const handleScroll = () => {
    const { isScroll } = state;
    if (window.scrollY === 0 && isScroll) {
      setState({ isScroll: false });
    } else if (window.scrollY !== 0 && !isScroll) {
      setState({ isScroll: true });
    }
  };

  /**
   * Opens the inbox ECG viewer for a clicked inbox row, or clears it when no row is given. Assembles the viewer payload from the row's study, patient, event time and facility timezone fields, raises the view-ECG loading text while fetching the clinic data, attaches the result to state, and logs plus toasts a failure before always clearing the loading state.
   *
   * @param clickedData - The inbox row to view; falsy clears the viewer payload.
   * @returns A promise resolving once the clinic data fetch settles.
   */
  const toggleInboxECGViewer = async (clickedData: any) => {
    if (clickedData) {
      loadingPageAction(LOADING_TYPES.VIEW_ECG);
      const inboxECGViewerData = {
        studyFid: clickedData.studyFid,
        patientName: clickedData.patientName,
        interprettingPhysicianName: clickedData.interprettingPhysicianName,
        startStudyDate: clickedData.study.start,
        eventTime: clickedData.eventTimeUTC,
        timezone: clickedData.timezone,
        studyTimeOffset: clickedData.study?.studyTimeOffset,
        facilityCountry: clickedData.study?.facility?.contact?.country,
        facilityTimezone: clickedData.study?.facility?.timezone,
      };
      try {
        const clinicData = await fetchClinicData({
          studyId: clickedData.study?.id,
          eventId: clickedData.eventId,
        });
        _.assign(inboxECGViewerData, { clinicData });
        setState({ inboxECGViewerData });
      } catch (error) {
        console.error('Failed to fetch clinic data: ', error);
        showMessageError({ content: error instanceof Error ? error.message : undefined });
      } finally {
        loadingPageAction();
      }
    } else {
      setState({ inboxECGViewerData: undefined });
    }
  };

  /**
   * Toggles the study-information or patient-information drawer of the inbox page and records the row that was clicked. The drawer title selects which visibility flag is inverted while any other title closes both drawers, and the merged object is written to state in a single call.
   *
   * @param clickedData - The inbox row whose details the drawer shows; null closes the drawers.
   * @param title - Drawer title deciding which visibility flag is inverted.
   * @returns Nothing.
   */
  const toggleDisplayDrawer = (clickedData: any = null, title = '') => {
    const setStateObject = { clickedData };

    switch (title) {
      case t`Study information`: {
        _.assign(setStateObject, {
          isVisibleStudyInfoDrawer: !state.isVisibleStudyInfoDrawer,
        });
        break;
      }
      case t`Patient information`: {
        _.assign(setStateObject, { isVisiblePatientInfoDrawer: !state.isVisiblePatientInfoDrawer });
        break;
      }
      default: {
        _.assign(setStateObject, {
          isVisibleStudyInfoDrawer: false,
          isVisiblePatientInfoDrawer: false,
        });
        break;
      }
    }

    setState(setStateObject);
  };

  /**
   * Records the unread count reported by the recent inbox list. Merges the number into hook state under recentUnreadCount.
   *
   * @param recentUnreadCount - Number of unread items in the recent inbox list.
   * @returns Nothing.
   */
  const updateRecentUnreadCount = (recentUnreadCount: number) => {
    setState({ recentUnreadCount });
  };

  /**
   * Records the total unreviewed report count reported by a child inbox list. Merges the number into state under unreviewedReportCount.
   *
   * @param inboxCount - Total number of unreviewed reports.
   * @returns Nothing.
   */
  const updateTotalUnreviewedInboxCount = (inboxCount: number) => {
    setState({ unreviewedReportCount: inboxCount });
  };

  useEffect(() => {
    document.title = t`Biocare Diagnostics - Inbox`;
  }, []);

  useEffect(() => {
    if (params?.reportType) {
      switch (state.activeTab) {
        case '1': {
          recentInboxListRef.current?.fetchData();
          break;
        }
        case '2': {
          allInboxListRef.current?.fetchData();
          break;
        }
        case '3': {
          eouInboxListRef.current?.fetchData();
          break;
        }
        case '4': {
          interimInboxListRef.current?.fetchData();
          break;
        }
        case '5': {
          eventInboxListRef.current?.fetchData();
          break;
        }
        case '6': {
          instantInboxListRef.current?.fetchData();
          break;
        }
        default: {
          break;
        }
      }
    }
  }, []);

  useEffect(() => {
    updateInboxCount();
    updateAllInboxCount();
  }, []);

  useEffect(() => {
    if (!inboxesGotoRedux?.tab) {
      setStateRedux({ tab: '1' });
    }
  }, []);

  useEffect(() => {
    /**
     * Emitter subscription that refreshes the inbox counts when an inbox item is marked. Registers a listener on the MARK_INBOX_EVENT channel whose callback re-runs the per-type and aggregate count queries, and its handle is removed on effect cleanup.
     *
     * @returns The emitter listener handle used to unsubscribe.
     */
    const markInboxListener = emitter.addListener(EMITTER_CONSTANTS.MARK_INBOX_EVENT, () => {
      updateInboxCount();
      updateAllInboxCount();
    });
    /**
     * Emitter subscription that refreshes the inbox counts whenever an inbox event is broadcast. Registers a listener on the INBOX_EVENTS channel whose callback re-runs the per-type and aggregate count queries, and its handle is removed on effect cleanup.
     *
     * @returns The emitter listener handle used to unsubscribe.
     */
    const inboxEventListener = emitter.addListener(EMITTER_CONSTANTS.INBOX_EVENTS, () => {
      updateInboxCount();
      updateAllInboxCount();
    });
    /**
     * Emitter subscription that refreshes the inbox counts when a report status is marked. Registers a listener on the MARK_STATUS_REPORT channel whose callback re-runs the per-type and aggregate count queries, and its handle is removed on effect cleanup.
     *
     * @returns The emitter listener handle used to unsubscribe.
     */
    const markStatusReportEventListener = emitter.addListener(
      EMITTER_CONSTANTS.MARK_STATUS_REPORT,
      () => {
        updateInboxCount();
        updateAllInboxCount();
      },
    );
    return () => {
      markInboxListener.remove();
      inboxEventListener.remove();
      markStatusReportEventListener.remove();
    };
  }, [selectedFacilityRedux, inboxesGotoRedux, state.searchGoto]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [state.isScroll]);

  useEffect(() => {
    if (headerRef.current) {
      if (auth.isShowLoginByBanner()) {
        headerRef.current.classList.add('has-login-banner');
      } else {
        headerRef.current.classList.remove('has-login-banner');
      }
    }
  }, [headerRef.current, isChangePosition]);

  useUpdateEffect(() => {
    const inboxDetailElement = document.getElementsByClassName('inbox-details');
    if (inboxDetailElement.length > 0) {
      if (state.inboxECGViewerData) {
        inboxDetailElement[0].classList.add('hidden');
      } else {
        inboxDetailElement[0].classList.remove('hidden');
      }
    }
  }, [state.inboxECGViewerData]);

  useUpdateDeepCompareEffect(() => {
    updateInboxCount();
  }, [
    state.searchGoto.searchValue,
    (state.searchGoto.physicianSearchValue as unknown as { value?: string })?.value,
    state.searchGoto.priority,
    state.searchGoto.statusReports,
    selectedFacilityRedux?.value,
    selectedTypeSeePatients?.value,
  ]);

  useUpdateDeepCompareEffect(() => {
    updateAllInboxCount();
  }, [
    state.searchGoto.searchValue,
    (state.searchGoto.physicianSearchValue as unknown as { value?: string })?.value,
    state.searchGoto.priority,
    state.searchGoto.statusAllReports,
    selectedFacilityRedux?.value,
    selectedTypeSeePatients?.value,
  ]);

  return {
    state,
    setState,
    params,
    inboxesGotoRedux,
    selectedFacilityRedux,
    selectedTypeSeePatients,
    allInboxListRef,
    recentInboxListRef,
    eouInboxListRef,
    interimInboxListRef,
    eventInboxListRef,
    instantInboxListRef,
    headerRef,
    isChangePosition,
    handleReloadInboxList,
    navigateTab,
    toggleInboxECGViewer,
    toggleDisplayDrawer,
    updateRecentUnreadCount,
    updateTotalUnreviewedInboxCount,
    updateSearchGoto,
  };
};

export { useInbox };
